/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    inject,
    OnInit,
    signal,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ButtonModule} from 'primeng/button';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {DialogModule} from 'primeng/dialog';
import {MessageModule} from 'primeng/message';
import {ProgressBarModule} from 'primeng/progressbar';
import {SelectModule} from 'primeng/select';
import {TooltipModule} from 'primeng/tooltip';
import {CellValueChangedEvent, ColDef, GridReadyEvent, ITooltipParams} from 'ag-grid-community';
import {HttpErrorResponse} from '@angular/common/http';
import {AgGridAngular} from 'ag-grid-angular';
import {finalize, map, switchMap} from 'rxjs';
import {EditableGridComponent} from '../editable-grid-component';
import {GridDownloadButtons} from '../grid-download-buttons';
import {CommonHelperService} from '../../service/common-helper.service';
import {ObjectDropdownCellEditorComponent} from '../cell-renderer/object-dropdown-cell-editor.component';
import {keywordDropdownOptions, keywordShowClear, keywordVisibility, normalizeKeyword} from '../aggregator-signal-table/aggregator-signal-key.helpers';
import {
    AggregatorAutoMatchRules,
    AggregatorIoMatrixDriftSummary,
    AggregatorIoMatrixRow,
    AggregatorOption
} from '../../model/aggregator-io-matrix.models';
import {PowerType} from '../../model/aggregator-signal.models';
import {
    AGGREGATOR_DEVICE_IO_DIFF_FIELDS,
    buildCadenceMergeRows,
    buildDeviceIoDiffRows,
    CadenceMergeMatchField,
    CadenceMergeRowMode,
    getIoMatrixMergeCellTooltip,
    renderCadenceMergeCellContent,
    wireDefaultIoMatrixMergeTooltips
} from '../../utils/cadence-merge.util';
import {buildMergeCellElement} from '../../utils/merge-cell-renderer.util';
import {
    handleIcdCompareKeydown,
    setExportingWithDetectChanges,
    subscribeIcdCompareDownload
} from '../../utils/icd-compare-export.util';
import {compareNaturalText} from '../../utils/natural-text.util';
import {ConfirmEnterAcceptDirective} from '../../utils/confirm-enter-accept.directive';

// Grid-visible device-IO columns only. twist / ioShield / networkGroup / pinType / circuitColor
// are intentionally NOT shown here (the user doesn't need them in the grid), but they remain on the
// AggregatorIoMatrixRow model — still loaded, persisted in the aggregator_io_matrix snapshot, and
// available to flow through to the ICD pins. Do not remove them from the model/backend.
const READ_ONLY_DEVICE_IO_COLUMNS: { field: string; headerName: string; minWidth: number }[] = [
    {field: 'deviceName', headerName: 'Device Name', minWidth: 200},
    {field: 'deviceIoName', headerName: 'Device IO Name', minWidth: 150},
    {field: 'deviceIoType', headerName: 'Device IO Type', minWidth: 120},
    {field: 'deviceIoValue', headerName: 'Device IO Value', minWidth: 130},
    {field: 'circuitNumber', headerName: 'Circuit Number', minWidth: 120},
    {field: 'circuitDesc', headerName: 'Circuit Desc', minWidth: 200}
];

// Columns whose values are resolved from the Aggregator Signal row. They are tinted green
// (program-column / program-column-header from styles.less; filter-row tint is scoped in
// aggregator-io-matrix.component.scss) so they read as a distinct group from device-IO columns.
// The fields an auto-match fills in. Clearing these on the "before" snapshot is what lets the standard
// change tracking see the suggestion as an edit rather than as loaded data.
const AUTO_MATCH_FILLED_FIELDS = ['portSignalName', 'connector', 'connectorPin', 'terminalSize',
                                  'swChannelId', 'ioTypeDesignator', 'partNumber',
                                  'pwmGroup', 'vRefMapping', 'flexibleDiscreteOutputs', 'terminalPlating'];

const AGGREGATOR_COLUMN_CELL_CLASS = 'program-column';
const AGGREGATOR_COLUMN_HEADER_CLASS = 'program-column-header';

const READ_ONLY_AGGREGATOR_COLUMNS: { field: string; headerName: string; minWidth: number }[] = [
    {field: 'connector', headerName: 'Connector', minWidth: 120},
    {field: 'terminalSize', headerName: 'Minimum Terminal Size', minWidth: 160},
    {field: 'swChannelId', headerName: 'SW Channel ID', minWidth: 130},
    {field: 'ioTypeDesignator', headerName: 'IO Type', minWidth: 120},
    {field: 'partNumber', headerName: 'Part Number', minWidth: 130},
    {field: 'pwmGroup', headerName: 'PWM Group', minWidth: 120},
    {field: 'vRefMapping', headerName: 'VRef Mapping', minWidth: 130},
    {field: 'flexibleDiscreteOutputs', headerName: 'Flexible Discrete Outputs', minWidth: 190},
    {field: 'terminalPlating', headerName: 'Terminal Plating', minWidth: 140}
];

/** Same candidate/closeness merge config as powertrain IO Matrix, adapted to flat aggregator row fields. */
const AGGREGATOR_CADENCE_MERGE_MATCH_FIELDS: CadenceMergeMatchField[] = [
    {path: 'deviceIoName', candidateMatch: true},
    {path: 'circuitNumber', candidateMatch: true},
    {path: 'deviceIoType', candidateMatch: false},
    {path: 'deviceIoValue', candidateMatch: false},
    {path: 'twist', candidateMatch: false},
    {path: 'ioShield', candidateMatch: false},
    {path: 'networkGroup', candidateMatch: false},
    {path: 'pinType', candidateMatch: false}
];

@Component({
    selector: 'app-aggregator-io-matrix',
    templateUrl: './aggregator-io-matrix.component.html',
    styleUrls: ['./aggregator-io-matrix.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, SelectModule, ButtonModule, ConfirmDialogModule, ConfirmEnterAcceptDirective,
              DialogModule, MessageModule, ProgressBarModule, TooltipModule, AgGridAngular]
})
export class AggregatorIoMatrixComponent extends EditableGridComponent implements OnInit, AfterViewChecked {
    @ViewChild(AgGridAngular, {read: ElementRef}) protected agGridHost?: ElementRef<HTMLElement>;
    paginationPageSize = 1000;
    paginationPageSizeSelector: number[] = [100, 500, 1000];
    private readonly downloadButtons = new GridDownloadButtons({
        canExportReport: () => this.canExportReport,
        canExportHistory: () => this.canExportHistory,
        onExportReport: () => this.exportReport(),
        onExportHistory: () => this.exportHistory()
    });

    autoMatching = false;

    readonly rulesDialogVisible = signal(false);
    readonly loadingRules = signal(false);
    readonly autoMatchRules = signal<AggregatorAutoMatchRules | null>(null);
    /** Configured CIRCUIT_PORT entries flattened for the dialog, the open matrix's object code first. */
    readonly circuitPortRuleRows = computed(() => {
        const current = this.objectCodeSignal().trim().toUpperCase();
        const byObjectCode = this.autoMatchRules()?.circuitPortRules || {};
        return Object.keys(byObjectCode)
            .sort((a, b) => Number(b === current) - Number(a === current) || compareNaturalText(a, b))
            .flatMap(objectCode => (byObjectCode[objectCode] || []).map(entry => ({objectCode, ...entry})));
    });
    /** Which signal each row was given by Auto Match, so a later hand-picked signal drops the rule label. */
    private readonly autoMatchedSignalIds = new WeakMap<AggregatorIoMatrixRow, number>();

    readonly objectCodesSignal = signal<string[]>([]);
    readonly architecturesSignal = signal<string[]>([]);
    readonly powerTypesSignal = signal<PowerType[]>([]);
    readonly objectCodeSignal = signal('');
    readonly architectureSignal = signal('');
    readonly powerTypeSignal = signal<PowerType | ''>('');
    readonly keywordSignal = signal('');
    readonly keywordOptionsSignal = signal<string[]>([]);
    readonly dataChangedSignal = signal(false);
    readonly refMapWarningSignal = signal(false);
    private refMapRequestId = 0;
    private powerTypeRequestId = 0;
    private keywordRequestId = 0;
    private optionsRequestId = 0;
    private cadenceRequestId = 0;

    // availableOptions for the Port/Signal Name + Connector Pin dropdown columns. Kept as a stable
    // array reference (cleared + repopulated in place) so the ColDefs built in buildColumns() keep
    // seeing updates.
    aggregatorOptions: AggregatorOption[] = [];
    private readonly aggregatorById = new Map<number, AggregatorOption>();
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.permissionUrl = '/esciUI/aggregatorIoMatrix/permission';
        this.saveUrl = this.esciService.saveAggregatorIoMatrixUrl;
        this.defaultColDef = wireDefaultIoMatrixMergeTooltips(this.defaultColDef);
        this.checkPermission();
        this.esciService.checkExportPermission(this);
        this.buildColumns();
        this.loadObjectCodes();
    }

    override refreshGrid(rows: any[], node?: any, onCompleteCallback?: () => void): void {
        super.refreshGrid(rows, node, onCompleteCallback);
        this.dataChangedSignal.set(this.dataChanged);
    }

    override cellEditingStopped(event: any, rows: any[]): void {
        super.cellEditingStopped(event, rows);
        this.dataChangedSignal.set(this.dataChanged);
    }

    override updateDataChanged(rows: any[]): void {
        super.updateDataChanged(rows);
        this.dataChangedSignal.set(this.dataChanged);
    }

    private setDirty(dirty: boolean): void {
        this.dataChanged = dirty;
        this.dataChangedSignal.set(dirty);
    }

    get objectCodes(): string[] {
        return this.objectCodesSignal();
    }

    get architectures(): string[] {
        return this.architecturesSignal();
    }

    get powerTypes(): PowerType[] {
        return this.powerTypesSignal();
    }

    get objectCode(): string {
        return this.objectCodeSignal();
    }

    set objectCode(value: string) {
        this.objectCodeSignal.set(value ?? '');
    }

    get architecture(): string {
        return this.architectureSignal();
    }

    set architecture(value: string) {
        this.architectureSignal.set(value ?? '');
    }

    get powerType(): PowerType | '' {
        return this.powerTypeSignal();
    }

    set powerType(value: PowerType | '') {
        this.powerTypeSignal.set(value ?? '');
    }

    get keyword(): string {
        return this.keywordSignal();
    }

    set keyword(value: string) {
        this.keywordSignal.set(normalizeKeyword(value));
    }

    readonly keywordOptions = computed(() => keywordDropdownOptions(this.keywordOptionsSignal()));
    readonly keywordVisible = computed(() => keywordVisibility(this.keywordOptionsSignal()) !== 'hidden');
    readonly keywordReadonly = computed(() => keywordVisibility(this.keywordOptionsSignal()) === 'readonly');
    readonly keywordShowClear = keywordShowClear;

    get contextLabel(): string {
        const keyword = this.keyword;
        return keyword
            ? `${this.objectCode} - ${this.architecture} - ${this.powerType} - ${keyword}`
            : `${this.objectCode} - ${this.architecture} - ${this.powerType}`;
    }

    /**
     * The cadence this scope's rows belong to.
     *
     * The matrix is versioned by cadence: a matrix saved against cadence 9 keeps those rows when the Device
     * IO source moves to 10, and the engineer chooses which cadence to open. Defaults to the latest saved
     * one - that is what they were last working on - not the source's newest.
     */
    readonly cadenceVersionsSignal = signal<number[]>([]);
    readonly savedCadenceVersionsSignal = signal<string[]>([]);
    readonly latestSavedCadenceVerSignal = signal('');
    readonly sourceNewestCadenceVerSignal = signal('');
    readonly selectedCadenceVerSignal = signal('');
    readonly upgradeAvailableSignal = signal(false);

    /**
     * A cadence upgrade in progress: the target cadence's rows are in the grid as unsaved edits with the
     * previous cadence's mappings carried over. Save writes them as a new cadence; Cancel drops back to the
     * saved one. Deliberately a review step rather than an automatic apply - a matrix is hundreds of rows and
     * a bad carry-over is invisible once persisted.
     */
    readonly mergePreviewActiveSignal = signal(false);
    readonly deviceIoDriftSummarySignal = signal<AggregatorIoMatrixDriftSummary | null>(null);
    readonly deviceIoSyncPreviewActiveSignal = signal(false);
    readonly deviceIoSyncSummarySignal = signal('');
    readonly mergeSourceCadenceVerSignal = signal('');
    readonly mergeSummarySignal = signal('');
    upgrading = false;
    syncingDeviceIo = false;
    /** When true, changed Device IO cells show current + struck-through original values (powertrain merge UX). */
    private inlineMergeOriginalValueDisplay = false;
    private lastLoadedSavedRows: AggregatorIoMatrixRow[] = [];
    private lastLoadedLiveRows: AggregatorIoMatrixRow[] = [];
    private loadRowsRequestId = 0;
    private autoMatchRequestId = 0;
    private autoMatchRulesRequestId = 0;

    readonly viewingLatestSavedCadence = computed(() => {
        const selected = this.selectedCadenceVerSignal();
        const latestSaved = this.latestSavedCadenceVerSignal();
        return !!selected && !!latestSaved && selected === latestSaved;
    });
    readonly viewingLiveDeviceIoCadence = computed(() => {
        const selected = this.selectedCadenceVerSignal();
        const live = this.sourceNewestCadenceVerSignal();
        return !!selected && !!live && selected === live;
    });
    readonly cadenceUpgradeOffered = computed(() =>
        this.upgradeAvailableSignal() && this.viewingLatestSavedCadence());
    readonly deviceIoDriftDetected = computed(() =>
        !!this.deviceIoDriftSummarySignal()?.driftDetected
        && !this.cadenceUpgradeOffered()
        && this.viewingLiveDeviceIoCadence());

    readonly anyMergePreviewActive = computed(() =>
        this.mergePreviewActiveSignal() || this.deviceIoSyncPreviewActiveSignal());

    /** Every cadence the engineer can open: saved ones plus the source's newest, newest first. */
    readonly cadenceOptionsSignal = computed(() => {
        const values = new Set<string>(this.savedCadenceVersionsSignal());
        const sourceNewest = this.sourceNewestCadenceVerSignal();
        if (sourceNewest) {
            values.add(sourceNewest);
        }
        return [...values]
            .sort((left, right) => Number(right) - Number(left) || right.localeCompare(left))
            .map(value => ({label: value, value}));
    });

    get cadenceLabel(): string {
        return this.selectedCadenceVerSignal() || 'not specified';
    }

    get canStartDeviceIoSync(): boolean {
        return this.deviceIoDriftDetected() && !this.anyMergePreviewActive()
            && !this.saving && !this.syncingDeviceIo && !this.upgrading && !this.autoMatching;
    }

    get canStartCadenceUpgrade(): boolean {
        // saving/upgrading/autoMatching are plain fields set from event handlers, so they belong in the
        // getter rather than the computed, which would cache them and never see them flip.
        return this.cadenceUpgradeOffered() && !this.anyMergePreviewActive()
            && !this.saving && !this.upgrading && !this.autoMatching;
    }

    get cadenceUpgradeLabel(): string {
        return `Upgrade to cadence ${this.sourceNewestCadenceVerSignal()}`;
    }

    /**
     * Switching cadence reloads the grid from the server, so it discards unsaved work exactly as starting an
     * upgrade would - and is gated the same way rather than silently throwing edits away.
     */
    get canChangeCadence(): boolean {
        return !this.dataChangedSignal() && !this.saving && !this.upgrading && !this.autoMatching;
    }

    private loadCadence(): void {
        const objectCode = this.objectCodeSignal();
        const architecture = this.architectureSignal();
        const powerType = this.powerTypeSignal();
        const keyword = this.keywordSignal();
        if (!objectCode || !architecture || !powerType) {
            this.cadenceRequestId++;
            this.resetCadenceState();
            return;
        }
        const requestId = ++this.cadenceRequestId;
        this.esciService.getAggregatorIoMatrixCadence(objectCode, architecture, powerType as string,
            keyword).subscribe({
            next: cadence => {
                if (requestId !== this.cadenceRequestId
                    || this.objectCodeSignal() !== objectCode
                    || this.architectureSignal() !== architecture
                    || this.powerTypeSignal() !== powerType
                    || this.keywordSignal() !== keyword) {
                    return;
                }
                const sourceNewest = cadence?.cadenceVersion == null ? '' : String(cadence.cadenceVersion);
                this.cadenceVersionsSignal.set((cadence?.cadenceVersions || []).filter(version => version != null));
                this.savedCadenceVersionsSignal.set(cadence?.savedCadenceVersions || []);
                this.latestSavedCadenceVerSignal.set(cadence?.latestSavedCadenceVer || '');
                this.sourceNewestCadenceVerSignal.set(sourceNewest);
                this.upgradeAvailableSignal.set(!!cadence?.upgradeAvailable);
                this.selectedCadenceVerSignal.set(cadence?.latestSavedCadenceVer || sourceNewest);
                this.loadRows();
            },
            error: () => {
                if (requestId !== this.cadenceRequestId
                    || this.objectCodeSignal() !== objectCode
                    || this.architectureSignal() !== architecture
                    || this.powerTypeSignal() !== powerType
                    || this.keywordSignal() !== keyword) {
                    return;
                }
                this.resetCadenceState();
                this.loadRows();
            }
        });
    }

    private resetCadenceState(): void {
        this.cadenceVersionsSignal.set([]);
        this.savedCadenceVersionsSignal.set([]);
        this.latestSavedCadenceVerSignal.set('');
        this.sourceNewestCadenceVerSignal.set('');
        this.selectedCadenceVerSignal.set('');
        this.upgradeAvailableSignal.set(false);
        this.mergePreviewActiveSignal.set(false);
        this.deviceIoDriftSummarySignal.set(null);
        this.deviceIoSyncPreviewActiveSignal.set(false);
        this.deviceIoSyncSummarySignal.set('');
        this.mergeSourceCadenceVerSignal.set('');
        this.mergeSummarySignal.set('');
        this.inlineMergeOriginalValueDisplay = false;
        this.lastLoadedSavedRows = [];
        this.lastLoadedLiveRows = [];
    }

    onCadenceChanged(): void {
        // Switching cadence abandons any merge preview: the preview belongs to one target cadence.
        this.mergePreviewActiveSignal.set(false);
        this.deviceIoSyncPreviewActiveSignal.set(false);
        this.deviceIoSyncSummarySignal.set('');
        this.loadRows();
    }

    /**
     * Loads the target cadence's Device IO rows, merges the saved snapshot at the source cadence on the
     * client (same flow as powertrain IO Matrix), and shows the result as unsaved edits.
     */
    startCadenceUpgrade(): void {
        if (this.autoMatching || this.saving) {
            return;
        }
        this.autoMatchRequestId++;
        const source = this.resolveUpgradeSourceCadence();
        const target = this.sourceNewestCadenceVerSignal();
        if (!source || !target || source === target) {
            this.messageService.processError('There is no newer Device IO cadence to upgrade to.');
            return;
        }
        if (this.dataChangedSignal()) {
            this.messageService.processError('Save or reset the current changes before upgrading the cadence.');
            return;
        }
        this.upgrading = true;
        this.esciService.cadenceUpgradeAggregatorIoMatrix(
            this.objectCode, this.architecture, this.powerType as string, target, this.keyword
        ).pipe(
            switchMap(targetRows => this.esciService.getAggregatorIoMatrixRows(
                this.objectCode, this.architecture, this.powerType as string, source, this.keyword
            ).pipe(
                // Deliberately no catchError: a snapshot that failed to load is not an empty snapshot.
                // Merging against zero saved rows marks every target row new and unmapped, and saving that
                // preview would drop every mapping the matrix already had.
                map(savedResponse => ({
                    targetRows: targetRows || [],
                    savedRows: savedResponse?.rows || [],
                    sourceCadenceVer: source,
                    targetCadenceVer: target
                }))
            )),
            finalize(() => {
                this.upgrading = false;
            })
        ).subscribe({
            next: ({targetRows, savedRows, sourceCadenceVer, targetCadenceVer}) => {
                if (!targetRows.length) {
                    this.messageService.processError(
                        `No Device IO rows were returned for cadence ${targetCadenceVer}. `
                        + 'Confirm the backend is running the latest build, then try again.');
                    return;
                }
                this.applyCadenceUpgradePreview(targetRows, savedRows, sourceCadenceVer, targetCadenceVer);
            },
            error: () => this.messageService.processError('Could not build the cadence upgrade preview.')
        });
    }

    /** Same source-cadence resolution as powertrain IO Matrix cadence upgrade. */
    private resolveUpgradeSourceCadence(): string {
        const latestSaved = this.latestSavedCadenceVerSignal();
        if (latestSaved) {
            return latestSaved;
        }
        const selected = this.selectedCadenceVerSignal();
        if (selected && this.savedCadenceVersionsSignal().includes(selected)) {
            return selected;
        }
        return '';
    }

    cancelCadenceUpgrade(): void {
        this.mergePreviewActiveSignal.set(false);
        this.mergeSourceCadenceVerSignal.set('');
        this.mergeSummarySignal.set('');
        this.inlineMergeOriginalValueDisplay = false;
        this.setDirty(false);
        this.selectedCadenceVerSignal.set(this.latestSavedCadenceVerSignal());
        this.loadRows();
    }

    startDeviceIoSync(): void {
        if (this.autoMatching || this.saving) {
            return;
        }
        this.autoMatchRequestId++;
        if (!this.deviceIoDriftDetected()) {
            this.messageService.processError('Device IO is already in sync with the saved matrix.');
            return;
        }
        if (this.dataChangedSignal()) {
            this.messageService.processError('Save or reset the current changes before syncing from Device IO.');
            return;
        }
        if (!this.lastLoadedSavedRows.length) {
            this.messageService.processError('Load a saved matrix before syncing from Device IO.');
            return;
        }
        this.syncingDeviceIo = true;
        try {
            const mergedRows = this.buildDeviceIoSyncRows(this.lastLoadedSavedRows, this.lastLoadedLiveRows);
            if (!mergedRows.length) {
                this.messageService.processError('Device IO sync produced no rows. Reload the matrix and try again.');
                return;
            }
            this.deviceIoSyncPreviewActiveSignal.set(true);
            this.deviceIoSyncSummarySignal.set(this.buildDeviceIoSyncSummary(mergedRows));
            this.inlineMergeOriginalValueDisplay = true;
            this.buildColumns();
            this.refreshGrid(mergedRows as any);
            this.autoSizeColumnsToContent();
            this.setDirty(true);
            if (this.isGridApiAlive(this.gridApi)) {
                this.gridApi.refreshCells({force: true});
                this.gridApi.redrawRows();
            }
        } finally {
            this.syncingDeviceIo = false;
        }
    }

    cancelDeviceIoSync(): void {
        this.deviceIoSyncPreviewActiveSignal.set(false);
        this.deviceIoSyncSummarySignal.set('');
        this.inlineMergeOriginalValueDisplay = false;
        this.setDirty(false);
        this.loadRows();
    }

    private buildDeviceIoSyncRows(savedRows: AggregatorIoMatrixRow[],
                                  liveRows: AggregatorIoMatrixRow[]): AggregatorIoMatrixRow[] {
        return buildDeviceIoDiffRows(
            savedRows,
            liveRows,
            AGGREGATOR_DEVICE_IO_DIFF_FIELDS,
            (row, mode, original) => this.decorateDeviceIoSyncRow(row, mode, original)
        );
    }

    private decorateDeviceIoSyncRow(row: AggregatorIoMatrixRow,
                                    mode: CadenceMergeRowMode,
                                    original?: AggregatorIoMatrixRow): AggregatorIoMatrixRow {
        if (mode === 'deleted') {
            row.state = 'deleted';
            row.changes = row.changes ?? new Map<string, string>();
            return row;
        }
        if (mode === 'new') {
            row.state = 'new';
            row.id = undefined;
            row.fkAggregatorSignalRowId = null;
            row.portSignalName = '';
            row.connector = '';
            row.connectorPin = '';
            row.terminalSize = '';
            row.swChannelId = '';
            row.ioTypeDesignator = '';
            row.partNumber = '';
            row.pwmGroup = '';
            row.vRefMapping = '';
            row.flexibleDiscreteOutputs = '';
            row.terminalPlating = '';
            row.changes = new Map<string, string>();
            return row;
        }
        if (mode === 'changed') {
            row.state = 'saved';
            row.changes = row.changes ?? new Map<string, string>();
            row.originalRowData = original ?? row.originalRowData;
            return row;
        }
        row.state = 'saved';
        row.changes = new Map<string, string>();
        row.originalRowData = original;
        return row;
    }

    private buildDeviceIoSyncSummary(rows: AggregatorIoMatrixRow[]): string {
        let changed = 0;
        let added = 0;
        let removed = 0;
        for (const row of rows || []) {
            if (row.state === 'new') {
                added++;
            } else if (row.state === 'deleted') {
                removed++;
            } else if (row.changes && row.changes.size > 0) {
                changed++;
            }
        }
        const parts = [
            changed ? `${changed} changed` : '',
            added ? `${added} new` : '',
            removed ? `${removed} removed` : ''
        ].filter(Boolean);
        return `Device IO sync preview: ${parts.join(', ') || 'no differences'}. Save to accept or Reset Changes to keep the saved snapshot.`;
    }

    private applyCadenceUpgradePreview(targetRows: AggregatorIoMatrixRow[], savedRows: AggregatorIoMatrixRow[],
                                       sourceCadenceVer: string, targetCadenceVer: string): void {
        const mergedRows = this.buildAggregatorCadenceMergeRows(targetRows, savedRows);
        if (!mergedRows.length) {
            this.messageService.processError(
                `Cadence upgrade produced no rows for ${targetCadenceVer}. `
                + 'Cancel and reload the matrix before trying again.');
            return;
        }
        const resolvedSource = sourceCadenceVer || this.resolveUpgradeSourceCadence();
        this.mergePreviewActiveSignal.set(true);
        this.mergeSourceCadenceVerSignal.set(resolvedSource);
        this.selectedCadenceVerSignal.set(targetCadenceVer);
        this.mergeSummarySignal.set(this.buildMergeSummaryFromRows(mergedRows, resolvedSource));
        this.inlineMergeOriginalValueDisplay = true;
        this.buildColumns();
        this.refreshGrid(mergedRows as any);
        this.autoSizeColumnsToContent();
        this.setDirty(true);
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.refreshCells({force: true});
            this.gridApi.redrawRows();
        }
    }

    private buildAggregatorCadenceMergeRows(latestRows: AggregatorIoMatrixRow[],
                                            savedRows: AggregatorIoMatrixRow[]): AggregatorIoMatrixRow[] {
        return buildCadenceMergeRows(
            latestRows,
            savedRows,
            AGGREGATOR_CADENCE_MERGE_MATCH_FIELDS,
            (latestRow, matchedSavedRow, mode) => this.decorateAggregatorMergeRow(latestRow, matchedSavedRow, mode)
        );
    }

    private decorateAggregatorMergeRow(latestRow: AggregatorIoMatrixRow,
                                       matchedSavedRow: AggregatorIoMatrixRow | undefined,
                                       mode: CadenceMergeRowMode): AggregatorIoMatrixRow {
        const baseRow = structuredClone(latestRow || {}) as AggregatorIoMatrixRow;
        const original = matchedSavedRow ? structuredClone(matchedSavedRow) : undefined;
        if (mode === 'matched' && matchedSavedRow) {
            baseRow.fkAggregatorSignalRowId = matchedSavedRow.fkAggregatorSignalRowId ?? null;
            baseRow.portSignalName = matchedSavedRow.portSignalName ?? '';
            baseRow.connector = matchedSavedRow.connector ?? '';
            baseRow.connectorPin = matchedSavedRow.connectorPin ?? '';
            baseRow.terminalSize = matchedSavedRow.terminalSize ?? '';
            baseRow.swChannelId = matchedSavedRow.swChannelId ?? '';
            baseRow.ioTypeDesignator = matchedSavedRow.ioTypeDesignator ?? '';
            baseRow.partNumber = matchedSavedRow.partNumber ?? '';
            baseRow.pwmGroup = matchedSavedRow.pwmGroup ?? '';
            baseRow.vRefMapping = matchedSavedRow.vRefMapping ?? '';
            baseRow.flexibleDiscreteOutputs = matchedSavedRow.flexibleDiscreteOutputs ?? '';
            baseRow.terminalPlating = matchedSavedRow.terminalPlating ?? '';
            baseRow.id = undefined;
            baseRow.state = 'saved';
        } else if (mode === 'deleted') {
            baseRow.state = 'deleted';
        } else {
            baseRow.state = 'new';
            baseRow.id = undefined;
            baseRow.fkAggregatorSignalRowId = null;
            baseRow.portSignalName = '';
            baseRow.connector = '';
            baseRow.connectorPin = '';
            baseRow.terminalSize = '';
            baseRow.swChannelId = '';
            baseRow.ioTypeDesignator = '';
            baseRow.partNumber = '';
            baseRow.pwmGroup = '';
            baseRow.vRefMapping = '';
            baseRow.flexibleDiscreteOutputs = '';
            baseRow.terminalPlating = '';
        }
        baseRow.changes = new Map<string, string>();
        baseRow.originalRowData = original;
        return baseRow;
    }

    private buildMergeSummaryFromRows(rows: AggregatorIoMatrixRow[], sourceCadenceVer: string): string {
        let carried = 0;
        let added = 0;
        const renamed: string[] = [];
        const removed: string[] = [];
        for (const row of rows || []) {
            if (row.state === 'new') {
                added++;
            }
            if (row.state === 'deleted' && this.hasSignal(row)) {
                removed.push(`${row.deviceIoName} (${row.circuitNumber})`);
            }
            if (row.state !== 'deleted' && row.state !== 'new' && this.hasSignal(row) && row.originalRowData) {
                carried++;
                const original = row.originalRowData as AggregatorIoMatrixRow;
                const oldName = String(original.deviceIoName ?? '').trim();
                const newName = String(row.deviceIoName ?? '').trim();
                if (oldName && newName && oldName !== newName) {
                    const circuit = String(row.circuitNumber ?? '').trim();
                    renamed.push(circuit
                        ? `${circuit} (${oldName} -> ${newName})`
                        : `${newName} (${oldName} -> ${newName})`);
                }
            }
        }
        const parts = [
            `${carried} mapping(s) carried over from cadence ${sourceCadenceVer}`,
            `${added} new pin(s) with no mapping yet`
        ];
        if (renamed.length > 0) {
            parts.push(`${renamed.length} pin(s) renamed, mapping kept: ${renamed.join(', ')}`);
        }
        if (removed.length > 0) {
            parts.push(`${removed.length} pin(s) no longer in this cadence, mapping lost: ${removed.join(', ')}`);
        }
        return parts.join('. ') + '.';
    }

    get scopeComplete(): boolean {
        return !!this.objectCode && !!this.architecture && !!this.powerType;
    }

    get refMapWarning(): boolean {
        return this.refMapWarningSignal();
    }

    get refMapWarningText(): string {
        return 'No Ref Mapping entry exists for this Object Code with a blank Object Code Inst. Save is still allowed, but ICD creation will be blocked until mapping exists.';
    }

    private refreshRefMapWarning(): void {
        const objectCode = this.objectCodeSignal();
        if (!objectCode) {
            this.refMapWarningSignal.set(false);
            this.refMapRequestId++;
            return;
        }
        const requestId = ++this.refMapRequestId;
        this.esciService.getAggregatorIoMatrixRefMapStatus(objectCode).subscribe({
            next: status => {
                if (requestId !== this.refMapRequestId) {
                    return;
                }
                const hasUsableMapping = !!(status && status.id != null && Number(status.id) > 0);
                this.refMapWarningSignal.set(!hasUsableMapping);
            },
            error: err => {
                if (requestId !== this.refMapRequestId) {
                    return;
                }
                this.refMapWarningSignal.set(false);
                this.messageService.processError(
                    err?.error?.message
                    || 'Could not load Reference Mapping status (request failed). Try again or contact support.');
            }
        });
    }

    get scopeSelectorsDisabled(): boolean {
        return this.dataChangedSignal() || this.saving || this.autoMatching || this.upgrading
            || this.anyMergePreviewActive();
    }

    private loadObjectCodes(): void {
        this.esciService.getAggregatorIoMatrixObjectCodes().subscribe(codes => {
            const list = (codes || []).map(code => String(code ?? '').trim()).filter(code => code.length > 0);
            this.objectCodesSignal.set(list);
        });
    }

    onObjectCodeChanged(): void {
        if (this.scopeSelectorsDisabled) {
            return;
        }
        this.architectureSignal.set('');
        this.powerTypeSignal.set('');
        this.keywordSignal.set('');
        this.architecturesSignal.set([]);
        this.powerTypesSignal.set([]);
        this.keywordOptionsSignal.set([]);
        this.clearGrid();
        this.refreshRefMapWarning();
        const objectCode = this.objectCodeSignal();
        if (!objectCode) {
            return;
        }
        this.esciService.getAggregatorIoMatrixArchitectures(objectCode).subscribe(architectures => {
            const list = architectures || [];
            this.architecturesSignal.set(list);
            if (list.length === 1) {
                this.architectureSignal.set(list[0]);
                this.onArchitectureChanged();
            }
        });
    }

    onArchitectureChanged(): void {
        if (this.scopeSelectorsDisabled) {
            return;
        }
        this.powerTypeSignal.set('');
        this.powerTypesSignal.set([]);
        this.keywordSignal.set('');
        this.keywordOptionsSignal.set([]);
        this.clearGrid();
        const objectCode = this.objectCodeSignal();
        const architecture = this.architectureSignal();
        if (!objectCode || !architecture) {
            this.powerTypeRequestId++;
            return;
        }
        const requestId = ++this.powerTypeRequestId;
        this.esciService.getAggregatorIoMatrixPowerTypes(objectCode, architecture).subscribe(powerTypes => {
            if (requestId !== this.powerTypeRequestId
                || this.objectCodeSignal() !== objectCode
                || this.architectureSignal() !== architecture) {
                return;
            }
            const list = (powerTypes || []) as PowerType[];
            this.powerTypesSignal.set(list);
            if (list.length === 1) {
                this.powerTypeSignal.set(list[0]);
                this.onPowerTypeChanged();
            }
        });
    }

    onPowerTypeChanged(): void {
        if (this.scopeSelectorsDisabled) {
            return;
        }
        this.keywordSignal.set('');
        this.keywordOptionsSignal.set([]);
        if (!this.objectCode || !this.architecture || !this.powerType) {
            this.keywordRequestId++;
            this.clearGrid();
            return;
        }
        const objectCode = this.objectCode;
        const architecture = this.architecture;
        const powerType = this.powerType;
        const requestId = ++this.keywordRequestId;
        this.esciService.getAggregatorIoMatrixKeywords(
            objectCode, architecture, powerType as string).subscribe(keywords => {
            if (requestId !== this.keywordRequestId
                || this.objectCode !== objectCode
                || this.architecture !== architecture
                || this.powerType !== powerType) {
                return;
            }
            const list = keywords || [];
            this.keywordOptionsSignal.set(list);
            const visibility = keywordVisibility(list);
            if (visibility === 'readonly') {
                this.keywordSignal.set(keywordDropdownOptions(list)[0] || '');
            } else if (visibility === 'dropdown') {
                this.keywordSignal.set(keywordDropdownOptions(list).includes('') ? '' : (keywordDropdownOptions(list)[0] || ''));
            } else {
                this.keywordSignal.set('');
            }
            this.loadAggregatorOptionsAndRows();
        });
    }

    onKeywordChanged(): void {
        if (this.scopeSelectorsDisabled) {
            return;
        }
        if (!this.scopeComplete) {
            this.clearGrid();
            return;
        }
        this.loadAggregatorOptionsAndRows();
    }

    private loadAggregatorOptionsAndRows(): void {
        const objectCode = this.objectCodeSignal();
        const architecture = this.architectureSignal();
        const powerType = this.powerTypeSignal();
        const keyword = this.keywordSignal();
        const requestId = ++this.optionsRequestId;
        this.esciService.getAggregatorIoMatrixAggregatorOptions(
            objectCode, architecture, powerType as string, keyword).subscribe(options => {
            if (requestId !== this.optionsRequestId
                || this.objectCodeSignal() !== objectCode
                || this.architectureSignal() !== architecture
                || this.powerTypeSignal() !== powerType
                || this.keywordSignal() !== keyword) {
                return;
            }
            this.aggregatorOptions.length = 0;
            this.aggregatorById.clear();
            for (const option of options || []) {
                this.aggregatorOptions.push(option);
                this.aggregatorById.set(option.id, option);
            }
            // Cadence first: it decides which rows to fetch, so loadCadence chains into loadRows.
            this.loadCadence();
        });
    }

    private loadRows(): void {
        this.autoMatchRequestId++;
        const objectCode = this.objectCodeSignal();
        const architecture = this.architectureSignal();
        const powerType = this.powerTypeSignal();
        const keyword = this.keywordSignal();
        const requestedCadence = this.selectedCadenceVerSignal();
        const requestId = ++this.loadRowsRequestId;
        this.esciService.getAggregatorIoMatrixRows(
            objectCode, architecture, powerType as string, requestedCadence, keyword
        ).subscribe(response => {
            if (requestId !== this.loadRowsRequestId
                || this.objectCodeSignal() !== objectCode
                || this.architectureSignal() !== architecture
                || this.powerTypeSignal() !== powerType
                || this.keywordSignal() !== keyword
                || this.selectedCadenceVerSignal() !== requestedCadence) {
                return;
            }
            const loaded = (response?.rows || []).map(row => this.tagLoaded(row));
            this.deviceIoDriftSummarySignal.set(response?.driftSummary ?? null);
            this.lastLoadedSavedRows = structuredClone(response?.rows || []);
            this.lastLoadedLiveRows = structuredClone(response?.liveDeviceIoRows || []);
            if (!this.deviceIoSyncPreviewActiveSignal()) {
                this.deviceIoSyncSummarySignal.set('');
            }
            this.refreshGrid(loaded as any);
            this.autoSizeColumnsToContent();
        });
    }

    /**
     * True once a scope is chosen and its rows are in the grid.
     *
     * Reads rowDataSignal rather than the plain rowData array on purpose: rows arrive asynchronously,
     * and a binding that depends only on a plain field does not update until something else triggers
     * change detection - which made the Auto Match button stay greyed out until the user clicked into
     * the grid. Reading the signal marks the view dirty as soon as the rows land.
     */
    readonly scopeHasRows = computed(() =>
        !!this.objectCodeSignal() && !!this.architectureSignal() && !!this.powerTypeSignal()
        && this.rowDataSignal().length > 0
    );

    get canAutoMatch(): boolean {
        // saving/autoMatching are plain fields set from event handlers, so they are checked here in the
        // getter (re-evaluated each change-detection pass) rather than inside the computed, which would
        // cache them and never see them flip.
        return this.scopeHasRows() && !this.saving && !this.autoMatching
            && !this.upgrading && !this.anyMergePreviewActive();
    }

    /**
     * Fills the empty rows with a signal suggested from the device IO name.
     *
     * Deliberately user-driven rather than automatic on load: a saved matrix should open showing what
     * was saved, not a grid of pending edits to dismiss. The grid's current rows are sent as-is so
     * unsaved edits survive, and the suggestions come back as ordinary unsaved changes - Save keeps
     * them, Reset Changes discards them.
     */
    autoMatch(): void {
        if (this.saving || this.autoMatching || this.upgrading || this.anyMergePreviewActive()) {
            return;
        }
        if (!this.scopeComplete) {
            this.messageService.processError('Select Object Code, Architecture, and Power Type first.');
            return;
        }
        const current = this.rowData as AggregatorIoMatrixRow[];
        // Remember which rows already carried a signal, so only the newly filled ones are presented as
        // edits. Tagging a row that was already saved would mark it dirty for no reason.
        const alreadyMatched = new Set(current.filter(row => this.hasSignal(row)).map(row => this.rowKey(row)));
        const unmatchedBefore = current.length - alreadyMatched.size;
        if (unmatchedBefore === 0) {
            this.messageService.processError('Every row already has a Port/Signal Name.');
            return;
        }
        this.autoMatching = true;
        const requestId = ++this.autoMatchRequestId;
        this.esciService.autoMatchAggregatorIoMatrix(
            this.objectCode, this.architecture, this.powerType as string, current, this.keyword).pipe(
            finalize(() => {
                if (requestId === this.autoMatchRequestId) {
                    this.autoMatching = false;
                }
            })
        ).subscribe({
            next: rows => {
                if (requestId !== this.autoMatchRequestId) {
                    return;
                }
                const returned = rows || [];
                // Only rows that gained a signal in this call are shown as edits; every other row keeps the
                // baseline, state and rule tooltip it had in the grid, so an earlier unsaved suggestion or
                // edit stays one across a second run.
                const newlyFilled = returned.filter(row => this.hasSignal(row) && !alreadyMatched.has(this.rowKey(row)));
                const filled = newlyFilled.length;
                const isNew = new Set(newlyFilled.map(row => this.rowKey(row)));
                const previousByKey = new Map(current.map(row => [this.rowKey(row), row]));
                const applied = returned.map(row => isNew.has(this.rowKey(row))
                    ? this.tagAutoMatched(row)
                    : this.carryOverGridState(row, previousByKey.get(this.rowKey(row))));
                this.refreshGrid(applied as any);
                this.autoSizeColumnsToContent();
                if (filled === 0) {
                    this.reportNoAutoMatch(unmatchedBefore);
                    return;
                }
                const byCircuit = newlyFilled.filter(row => row.autoMatchRule === 'CIRCUIT_PORT').length;
                // Carries its own leading separator: " by the ..." or ": 1 by ..., 2 by ...".
                const sources = AggregatorIoMatrixComponent.autoMatchSources(byCircuit, filled - byCircuit);
                this.messageService.processSuccess(
                    `${filled} of ${unmatchedBefore} empty rows matched${sources}. `
                    + `Save to keep them, or Reset Changes to discard.`);
            },
            error: (err: any) => {
                if (requestId !== this.autoMatchRequestId) {
                    return;
                }
                this.messageService.processError(err?.error?.message || 'Auto match failed.');
            }
        });
    }

    /** Opens the read-only rules list. Fetched on every open so an admin's edit shows without a reload. */
    openAutoMatchRules(): void {
        this.rulesDialogVisible.set(true);
        this.loadingRules.set(true);
        // Drop the last open's rules, so a failed fetch cannot show them as current.
        this.autoMatchRules.set(null);
        // Only the latest open may write: an older response arriving late must not replace newer rules.
        this.autoMatchRulesRequestId++;
        const requestId = this.autoMatchRulesRequestId;
        const isLatest = () => requestId === this.autoMatchRulesRequestId;
        this.esciService.getAggregatorIoMatrixAutoMatchRules().pipe(
            finalize(() => {
                if (isLatest()) {
                    this.loadingRules.set(false);
                }
            })
        ).subscribe({
            next: rules => {
                if (isLatest()) {
                    this.autoMatchRules.set(rules);
                }
            },
            error: (err: HttpErrorResponse) => {
                if (isLatest()) {
                    this.messageService.processError(err?.error?.message || 'Could not load the Auto Match rules.');
                }
            }
        });
    }

    onAutoMatchKeydown(event: KeyboardEvent): void {
        handleIcdCompareKeydown(event, () => this.autoMatch());
    }

    onViewRulesKeydown(event: KeyboardEvent): void {
        handleIcdCompareKeydown(event, () => this.openAutoMatchRules());
    }

    private autoMatchRuleTooltip(row: AggregatorIoMatrixRow): string | null {
        const suggested = row ? this.autoMatchedSignalIds.get(row) : undefined;
        return row?.autoMatchRule && suggested != null && suggested === row.fkAggregatorSignalRowId
            ? `Suggested by Auto Match rule ${row.autoMatchRule}`
            : null;
    }

    /**
     * Rules applied but matched nothing. When no rules apply at all the server answers the Auto Match request
     * itself with an error saying so, so no second request (possibly to a server caching other rules) is needed.
     */
    private reportNoAutoMatch(unmatchedBefore: number): void {
        this.messageService.processError(
            `No further rows could be matched by the Auto Match rules. `
            + `${unmatchedBefore} still need a Port/Signal Name chosen manually.`);
    }

    private static autoMatchSources(byCircuit: number, byOtherRules: number): string {
        if (byOtherRules === 0) {
            return ' by the circuit-to-port rule';
        }
        if (byCircuit === 0) {
            return ' by the Auto Match rules';
        }
        return `: ${byCircuit} by the circuit-to-port rule, ${byOtherRules} by the other Auto Match rules`;
    }

    private hasSignal(row: AggregatorIoMatrixRow): boolean {
        const fk = row?.fkAggregatorSignalRowId;
        return fk != null && fk > 0;
    }

    /**
     * Identifies a device IO row across the request/response boundary, where ids may not exist yet.
     *
     * <p>Mirrors the server's {@code AggregatorIoMatrixRow.deviceIoKey()}, pin type included: two rows can
     * be identical apart from it, and folding them together here would write off the second row's
     * suggestion as one the grid already had.
     */
    private rowKey(row: AggregatorIoMatrixRow): string {
        return [row?.objectCodeInst, row?.deviceName, row?.deviceIoName, row?.pinType, row?.circuitNumber]
            .map(part => (part ?? '').toString().trim())
            .join('|');
    }

    private clearGrid(): void {
        this.refreshGrid([] as any);
        this.setDirty(false);
    }

    private tagLoaded(row: AggregatorIoMatrixRow): AggregatorIoMatrixRow {
        row.state = 'saved';
        row.changes = new Map<string, string>();
        row.originalRowData = {...row};
        return row;
    }

    // A row the server auto-matched arrives with a suggested signal already applied. Recording the
    // PRE-suggestion state as originalRowData is what makes the suggestion behave like a normal edit:
    // refreshGrid's updateDataChanged then diffs the two, marks the row changed, enables Save, and
    // gives Reset something to fall back to. Nothing is persisted until the user saves.
    private tagAutoMatched(row: AggregatorIoMatrixRow): AggregatorIoMatrixRow {
        const unmatched: any = {...row, fkAggregatorSignalRowId: null};
        for (const field of AUTO_MATCH_FILLED_FIELDS) {
            unmatched[field] = '';
        }
        row.state = 'saved';
        row.changes = new Map<string, string>();
        row.originalRowData = unmatched;
        if (row.fkAggregatorSignalRowId != null) {
            this.autoMatchedSignalIds.set(row, row.fkAggregatorSignalRowId);
        }
        return row;
    }

    /**
     * A row Auto Match returned without filling takes over the grid state of the row it replaces: the server
     * sends back fresh objects, and re-tagging them as loaded would make any unsaved suggestion or edit the
     * saved baseline and drop the suggestion's rule tooltip. A row with no tracked predecessor is tagged as loaded.
     */
    private carryOverGridState(row: AggregatorIoMatrixRow, previous?: AggregatorIoMatrixRow): AggregatorIoMatrixRow {
        if (!previous?.originalRowData) {
            return this.tagLoaded(row);
        }
        row.state = previous.state;
        row.changes = previous.changes;
        row.originalRowData = previous.originalRowData;
        const suggested = this.autoMatchedSignalIds.get(previous);
        if (suggested != null) {
            this.autoMatchedSignalIds.set(row, suggested);
        }
        return row;
    }

    private checkPermission(): void {
        this.esciService.checkAggregatorIoMatrixPermission().subscribe(allowed => this.permission.set(!!allowed));
    }

    private buildColumns(): void {
        const deviceIoCols: ColDef[] = READ_ONLY_DEVICE_IO_COLUMNS.map(({field, headerName, minWidth}) => ({
            field, headerName, minWidth, editable: false
        }));
        this.applyMergeCellPresentation(deviceIoCols);
        const cols: ColDef[] = [
            ...deviceIoCols,
            // Both editable dropdowns resolve the SAME aggregator option; the user can drive the row
            // from either Port/Signal Name (non-unique — many 'NC') or the unique Connector Pin.
            this.buildAggregatorDropdownColumn('portSignalName', 'Port/Signal Name', 160,
                '${val.portSignalName} — ${val.connectorPin}'),
            this.buildAggregatorDropdownColumn('connectorPin', 'Connector Pin', 140,
                '${val.connectorPin} — ${val.portSignalName}'),
            ...READ_ONLY_AGGREGATOR_COLUMNS.map(({field, headerName, minWidth}) => ({
                field, headerName, minWidth, editable: false,
                cellClass: AGGREGATOR_COLUMN_CELL_CLASS,
                headerClass: AGGREGATOR_COLUMN_HEADER_CLASS,
                ...(field === 'connector' ? {comparator: compareNaturalText} : {})
            }))
        ];
        this.showAutoMatchRuleTooltip(cols);
        this.cols = cols;
    }

    private showAutoMatchRuleTooltip(cols: ColDef[]): void {
        const portSignalCol = cols.find(col => col.field === 'portSignalName');
        if (portSignalCol) {
            portSignalCol.tooltipValueGetter = (params: ITooltipParams<AggregatorIoMatrixRow>) =>
                this.autoMatchRuleTooltip(params.data) ?? getIoMatrixMergeCellTooltip(params, 'portSignalName');
        }
    }

    /** Device IO columns only — same scope as powertrain merge inline display (not dropdown columns). */
    private applyMergeCellPresentation(columns: ColDef[]): void {
        for (const col of columns || []) {
            const colId = col.colId || '';
            if (col.cellDataType === 'boolean' || colId.startsWith('variant_')
                || col.cellEditor || col.cellEditorSelector) {
                continue;
            }
            const field = col.field || '';
            col.tooltipValueGetter = (params: any) => getIoMatrixMergeCellTooltip(params, field);
            col.cellRenderer = (params: any) => this.renderMergeCellContent(params, field);
        }
    }

    // Always hand ag-grid an element: a string returned from cellRenderer is assigned as innerHTML, so
    // returning a Device IO value directly would let a crafted name or description inject grid nodes.
    private renderMergeCellContent(params: any, field: string): HTMLElement {
        const value = params?.valueFormatted ?? params?.value ?? '';
        const displayValue = String(value ?? '');
        if (!this.inlineMergeOriginalValueDisplay || !field) {
            return buildMergeCellElement(displayValue, '');
        }
        const rendered = renderCadenceMergeCellContent(displayValue, params?.data?.changes?.get(field));
        return typeof rendered === 'string' ? buildMergeCellElement(rendered, '') : rendered;
    }

    // A green, editable aggregator dropdown column. The editor commits the FULL option object (no
    // isListManagerStructure) so the row resolves the exact signal by unique id; valueFormatString
    // gives each list entry a combined label so duplicate port names stay distinguishable, and filter
    // turns on the typeahead search box.
    private buildAggregatorDropdownColumn(field: string, headerName: string, minWidth: number,
                                          valueFormatString: string): ColDef {
        const params = {
            availableOptions: this.aggregatorOptions,
            allowedValueKey: field,
            valueFormatString,
            maxSelectionCount: 1,
            memberType: 'objectDropdown',
            filter: true,
            // Without the clear control a cell can only be swapped for another signal, never emptied,
            // so a wrong pick (or an auto-match the user rejects for one row) could not be undone.
            // Clearing commits null, which applyAggregatorSelection already treats as "unmap this row".
            allowClear: true
        };
        return {
            field, headerName, editable: true, minWidth,
            cellClass: AGGREGATOR_COLUMN_CELL_CLASS,
            headerClass: AGGREGATOR_COLUMN_HEADER_CLASS,
            ...(field === 'connectorPin' ? {comparator: compareNaturalText} : {}),
            // Disable ag-grid's cell-data-type check: the editor commits the full option OBJECT, which
            // a 'text' column would reject ("data type does not match") before the valueSetter runs.
            cellDataType: false,
            valueFormatter: (p) => this.aggregatorFieldDisplayValue(p?.value, field),
            cellEditor: ObjectDropdownCellEditorComponent,
            cellEditorParams: params,
            // The whole row fill happens HERE: the valueSetter is the only hook that receives the
            // committed option OBJECT (params.newValue). The grid-level cellValueChanged event only
            // carries the read-back display string, which cannot disambiguate duplicate port names.
            valueSetter: (p: any) => {
                const row = p.data as AggregatorIoMatrixRow;
                // A hand-picked signal is the engineer's choice, even when it is the one Auto Match suggested.
                this.autoMatchedSignalIds.delete(row);
                this.applyAggregatorSelection(row, p.newValue);
                return true;
            },
            // The option list is built per cell rather than once per column, because which signals are
            // still free depends on what the other rows have already taken. cellEditorSelector is the
            // only hook that receives the row being edited.
            cellEditorSelector: (p: any) => ({
                component: ObjectDropdownCellEditorComponent,
                params: {...params, availableOptions: this.selectableOptionsFor(p?.data)}
            })
        };
    }

    /**
     * The signals this row may still choose from: everything not already assigned to another row, plus
     * whatever this row currently holds so its own value is never hidden from it.
     *
     * One aggregator pin is physically one wire, so offering a signal that another row has taken invites
     * a mapping that cannot exist. Save rejects duplicates too - this keeps the user from reaching that
     * error in the first place, and shrinks a list of a couple of hundred signals to the ones in play.
     *
     * The row's own value is re-admitted explicitly rather than merely skipping this row while collecting
     * the taken set: when a duplicate exists, another row contributes the same signal and would filter it
     * back out, leaving the dropdown blank on a row that plainly has a value and no way to correct it. Save
     * rejects duplicates, so they arrive from a cadence upgrade preview - two source rows sharing a
     * deviceIoKey both inherit the same carried-over mapping - or from data predating that guard.
     */
    private selectableOptionsFor(editedRow: AggregatorIoMatrixRow | null | undefined): AggregatorOption[] {
        const takenByOtherRows = new Set<number>();
        for (const row of this.rowData as AggregatorIoMatrixRow[]) {
            if (row === editedRow || row?.state === 'deleted') {
                continue;
            }
            const fk = row?.fkAggregatorSignalRowId;
            if (fk != null && fk > 0) {
                takenByOtherRows.add(fk);
            }
        }
        const currentlyHeld = editedRow?.fkAggregatorSignalRowId;
        return this.aggregatorOptions.filter(option =>
            !takenByOtherRows.has(option.id) || option.id === currentlyHeld);
    }

    onCellValueChanged(event: CellValueChangedEvent): void {
        // Fill is done in the dropdown columns' valueSetter (it receives the option object; this event
        // only carries the read-back string). Here we just keep Save/Reset state and sibling cells in sync.
        if (!event?.data) {
            return;
        }
        this.updateDataChanged(this.rowData as any);
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.refreshCells({force: true});
        }
    }

    private applyAggregatorSelection(editedRow: AggregatorIoMatrixRow, picked: unknown): void {
        const resolved = this.resolveAggregatorOption(picked);
        if (resolved) {
            editedRow.fkAggregatorSignalRowId = resolved.id;
            editedRow.portSignalName = resolved.portSignalName;
            editedRow.connector = resolved.connector;
            editedRow.connectorPin = resolved.connectorPin;
            editedRow.terminalSize = resolved.terminalSize ?? '';
            editedRow.swChannelId = resolved.swChannelId ?? '';
            editedRow.ioTypeDesignator = resolved.ioTypeDesignator ?? '';
            editedRow.partNumber = resolved.partNumber ?? '';
            editedRow.pwmGroup = resolved.pwmGroup ?? '';
            editedRow.vRefMapping = resolved.vRefMapping ?? '';
            editedRow.flexibleDiscreteOutputs = resolved.flexibleDiscreteOutputs ?? '';
            editedRow.terminalPlating = resolved.terminalPlating ?? '';
        } else {
            editedRow.fkAggregatorSignalRowId = null;
            editedRow.portSignalName = '';
            editedRow.connector = '';
            editedRow.connectorPin = '';
            editedRow.terminalSize = '';
            editedRow.swChannelId = '';
            editedRow.ioTypeDesignator = '';
            editedRow.partNumber = '';
            editedRow.pwmGroup = '';
            editedRow.vRefMapping = '';
            editedRow.flexibleDiscreteOutputs = '';
            editedRow.terminalPlating = '';
        }
    }

    // Renders the given field's plain value for a cell, whether the stored value is already a string
    // or the full option object committed by the dropdown editor.
    private aggregatorFieldDisplayValue(value: unknown, field: string): string {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (value && typeof value === 'object' && field in (value as Record<string, unknown>)) {
            return String((value as Record<string, unknown>)[field] ?? '').trim();
        }
        return '';
    }

    private resolveAggregatorOption(picked: unknown): AggregatorOption | undefined {
        if (picked && typeof picked === 'object') {
            return picked as AggregatorOption;
        }
        if (picked !== null && picked !== undefined && picked !== '') {
            const asId = Number(picked);
            if (!Number.isNaN(asId)) {
                return this.aggregatorById.get(asId);
            }
        }
        return undefined;
    }

    save(): void {
        if (this.autoMatching || this.saving) {
            return;
        }
        if (!this.areRowsValid(this.rowData as any)) {
            return;
        }
        const activeRows = (this.rowData as AggregatorIoMatrixRow[]).filter(row => row.state !== 'deleted');
        this.saving = true;
        this.esciService.saveAggregatorIoMatrix(this.objectCode, this.architecture, this.powerType as string,
            this.selectedCadenceVerSignal(), this.mergePreviewActiveSignal(),
            this.mergeSourceCadenceVerSignal(), activeRows, this.keyword).pipe(
            finalize(() => {
                this.saving = false;
            })
        ).subscribe({
            next: () => {
                this.messageService.processSuccess('Aggregator IO Matrix saved successfully.');
                this.markRowsSavedAfterSave();
                this.mergePreviewActiveSignal.set(false);
                this.deviceIoSyncPreviewActiveSignal.set(false);
                this.deviceIoSyncSummarySignal.set('');
                this.mergeSourceCadenceVerSignal.set('');
                this.mergeSummarySignal.set('');
                this.inlineMergeOriginalValueDisplay = false;
                // loadCadence, not loadRows: the save may have moved the latest saved cadence and cleared
                // the upgrade banner, and only the cadence call knows that.
                this.loadCadence();
            },
            error: (err: any) => {
                this.messageService.processError(err?.error?.message || 'Save failed.');
            }
        });
    }

    private markRowsSavedAfterSave(): void {
        const activeRows = (this.rowData as AggregatorIoMatrixRow[]).filter(row => row.state !== 'deleted');
        for (const row of activeRows) {
            this.tagLoaded(row);
        }
        this.setDirty(false);
        this.rowsInvalid = false;
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.refreshCells({force: true});
        }
    }

    resetChanges(): void {
        if (this.autoMatching) {
            return;
        }
        if (this.mergePreviewActiveSignal()) {
            this.cancelCadenceUpgrade();
            return;
        }
        if (this.deviceIoSyncPreviewActiveSignal()) {
            this.cancelDeviceIoSync();
            return;
        }
        if (!this.scopeComplete) {
            this.clearGrid();
            return;
        }
        this.loadRows();
    }

    ngAfterViewChecked(): void {
        if (this.permission() && !this.downloadButtons.isConnected) {
            this.ensureDownloadButtonsInFooter();
        }
        this.downloadButtons.updateDisabledState();
    }

    protected override afterPrimaryGridReady(_params: GridReadyEvent): void {
        this.ensureDownloadButtonsInFooter();
        this.autoSizeColumnsToContent();
        this.syncAggregatorFilterRowColIds();
    }

    private syncAggregatorFilterRowColIds(): void {
        this.syncFilterRowColIds(this.agGridHost?.nativeElement);
    }

    // Size every column to fit its own content (NOT the grid width) so long values like Device Name
    // and Circuit Desc stay on one readable line; the grid scrolls horizontally when the total
    // width exceeds the viewport. Deliberately no sizeColumnsToFit() here — that would stretch the
    // columns back to the grid width and re-wrap the long cells.
    protected autoSizeColumnsToContent(): void {
        setTimeout(() => {
            if (!this.isGridApiAlive(this.gridApi)) {
                return;
            }
            const api: any = this.gridApi as any;
            if (typeof api.autoSizeAllColumns === 'function') {
                api.autoSizeAllColumns(false);
            }
            this.syncAggregatorFilterRowColIds();
        }, 0);
    }

    get canExportReport(): boolean {
        // Gated on scopeHasRows, which reads rowDataSignal, for the same reason canAutoMatch is: the grid
        // API's displayed-row count and the plain rowData array are not signals, so reading either left the
        // footer button disabled until an unrelated click forced change detection. canExportHistory was
        // never affected because scopeComplete reads the scope signals.
        //
        // A filter that hides every row no longer disables the button; exportReport() already refuses with
        // an explanatory message in that case, which beats a button that is dead for no visible reason.
        return !this.exporting && this.scopeHasRows() && !this.anyMergePreviewActive();
    }

    get canExportHistory(): boolean {
        return !this.exporting && this.scopeComplete && !this.anyMergePreviewActive();
    }

    get canExportIcdCompare(): boolean {
        return this.canExport()
            && this.canExportReport
            && !this.dataChangedSignal()
            && !this.saving
            && this.hasPersistedMatrixForSelectedCadence();
    }

    /** ICD Compare reads persisted rows only; a live-seeded grid is not a compare snapshot. */
    private hasPersistedMatrixForSelectedCadence(): boolean {
        const selected = this.selectedCadenceVerSignal();
        return !!selected && this.savedCadenceVersionsSignal().includes(selected);
    }

    exportReport(): void {
        if (!this.isGridApiAlive(this.gridApi) || this.gridApi.getDisplayedRowCount() < 1) {
            this.messageService.processError('There are no rows to export.');
            return;
        }
        this.excelService.exportGridDataAsExcelFile(this.gridApi, this.getReportFileNamePrefix(), this.getExportTabName());
    }

    onIcdCompareKeydown(event: KeyboardEvent): void {
        handleIcdCompareKeydown(event, () => this.exportIcdCompare());
    }

    exportIcdCompare(): void {
        if (!this.canExportIcdCompare) {
            this.messageService.processError(
                'Select a matrix scope with rows before downloading the ICD compare.');
            return;
        }
        const objectCode = this.objectCode;
        const architecture = this.architecture;
        const powerType = this.powerType as string;
        const cadenceVer = this.selectedCadenceVerSignal();
        const keywordPart = this.keyword ? `-${this.keyword}` : '';
        subscribeIcdCompareDownload(
            this.esciService.getAggregatorIoMatrixIcdCompare(objectCode, architecture, powerType, cadenceVer, this.keyword),
            {
                setExporting: value => this.setExporting(value),
                fileName: `AggregatorIoMatrixIcdCompare-${objectCode}-${architecture}-${powerType}-${cadenceVer}${keywordPart}-${CommonHelperService.getTimeStampForReport()}.xlsx`,
                onEmpty: message => this.messageService.processError(message),
                onError: message => this.messageService.processError(message)
            }
        );
    }

    exportHistory(): void {
        if (!this.scopeComplete) {
            this.messageService.processError('Select Object Code, Architecture, and Power Type first.');
            return;
        }
        this.exporting = true;
        this.esciService.getAggregatorIoMatrixHistory(this.objectCode, this.architecture, this.powerType as string,
            this.keyword).subscribe({
            next: (data: ArrayBuffer) => {
                this.exporting = false;
                if (!data || data.byteLength < 1) {
                    this.messageService.processError('No history to export for this key yet.');
                    return;
                }
                const fileName = `${this.getHistoryFileNamePrefix()}-${CommonHelperService.getTimeStampForReport()}.xlsx`;
                CommonHelperService.downloadFile(new Blob([data], {type: 'application/octet-stream;charset=utf-8'}), fileName);
            },
            error: (err: any) => {
                this.exporting = false;
                this.messageService.processError(err?.error?.message || 'History export failed.');
            }
        });
    }

    private setExporting(value: boolean): void {
        setExportingWithDetectChanges(this, this.changeDetectorRef, value);
    }

    private ensureDownloadButtonsInFooter(): void {
        if (!this.permission()) {
            return;
        }
        this.downloadButtons.ensureInFooter(this.agGridHost?.nativeElement,
            (node, gridHost) => this.insertNodeIntoAgGridPaginationPanel(node, gridHost));
    }

    private getReportFileNamePrefix(): string {
        return this.getExportFileNamePrefix('AggregatorIoMatrixReport');
    }

    private getHistoryFileNamePrefix(): string {
        return this.getExportFileNamePrefix('AggregatorIoMatrixHistory');
    }

    get canDeleteMatrix(): boolean {
        return this.scopeComplete && !this.saving && !this.dataChangedSignal() && !this.createModeLikePreview()
            && !!this.selectedCadenceVerSignal() && !this.autoMatching && !this.upgrading;
    }

    get canExportHistoryAll(): boolean {
        return this.canExport() && !this.exporting;
    }

    private createModeLikePreview(): boolean {
        return this.anyMergePreviewActive();
    }

    confirmDeleteMatrix(): void {
        if (!this.canDeleteMatrix) {
            return;
        }
        this.confirmationService.confirm({
            key: 'deleteMatrixConfirmation',
            message: `Delete the IO matrix for ${this.contextLabel} at cadence ${this.selectedCadenceVerSignal()}? `
                + 'This cadence only. Mapped rows are deleted. This cannot be undone.',
            header: 'Delete Matrix',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptButtonProps: {severity: 'danger'},
            accept: () => this.deleteMatrix()
        });
    }

    deleteMatrix(): void {
        if (!this.canDeleteMatrix) {
            return;
        }
        this.autoMatchRequestId++;
        this.saving = true;
        this.esciService.deleteAggregatorIoMatrix(
            this.objectCode, this.architecture, this.powerType as string,
            this.selectedCadenceVerSignal(), this.keyword).pipe(
            finalize(() => {
                this.saving = false;
            })
        ).subscribe({
            next: () => {
                this.messageService.processSuccess('Aggregator IO Matrix deleted for this cadence.');
                this.loadCadence();
            },
            error: (err: any) => {
                this.messageService.processError(err?.error?.message || 'Delete Matrix failed.');
            }
        });
    }

    exportHistoryAll(): void {
        if (!this.canExportHistoryAll) {
            this.messageService.processError('User does not have permission to download all history.');
            return;
        }
        this.setExporting(true);
        this.esciService.getAggregatorIoMatrixHistoryAll()
            .pipe(finalize(() => this.setExporting(false)))
            .subscribe({
                next: (data: ArrayBuffer) => {
                    this.setExporting(false);
                    if (!data || data.byteLength < 1) {
                        this.messageService.processError('No history to export.');
                        return;
                    }
                    const fileName = `AggregatorIoMatrixHistoryAll-${CommonHelperService.getTimeStampForReport()}.xlsx`;
                    CommonHelperService.downloadFile(new Blob([data], {type: 'application/octet-stream;charset=utf-8'}), fileName);
                },
                error: (err: any) => {
                    this.messageService.processError(err?.error?.message || 'All-history export failed.');
                }
            });
    }

    private getExportFileNamePrefix(baseName: string): string {
        if (this.scopeComplete) {
            const keyword = this.keyword;
            return keyword
                ? `${baseName}-${this.objectCode}-${this.architecture}-${this.powerType}-${keyword}`
                : `${baseName}-${this.objectCode}-${this.architecture}-${this.powerType}`;
        }
        return baseName;
    }

    private getExportTabName(): string {
        if (!this.scopeComplete) {
            return 'Aggregator IO Matrix';
        }
        return this.keyword
            ? `${this.objectCode} - ${this.architecture} - ${this.powerType} - ${this.keyword}`
            : `${this.objectCode} - ${this.architecture} - ${this.powerType}`;
    }
}
